/**
 * ProfilingProgress Component
 *
 * Displays a progress bar showing progress through post-conversion qualification.
 * Filled portion = answered questions, remaining = empty.
 */

import type React from 'react';
import { hexToRgba } from '../utils/ui/colorUtils';

interface ProfilingProgressProps {
    currentQuestion: number;
    totalFields: number;
    primaryColor: string;
}

export const ProfilingProgress: React.FC<ProfilingProgressProps> = ({
    currentQuestion,
    totalFields,
    primaryColor,
}) => {
    if (totalFields <= 0) return null;
    const progressPercent = (currentQuestion / totalFields) * 100;

    return (
        <div
            className="rose-profiling-progress mb-3"
            style={{
                animation: 'roseProfilingProgressSlideIn 0.5s ease-out 0.3s forwards',
                opacity: 0,
            }}
            aria-label={`Question ${currentQuestion} of ${totalFields}`}
            role="progressbar"
            aria-valuenow={currentQuestion}
            aria-valuemin={1}
            aria-valuemax={totalFields}
        >
            <div className="flex items-center gap-2">
                <div
                    className="flex-1 rounded-full overflow-hidden"
                    style={{
                        height: 4,
                        backgroundColor: hexToRgba(primaryColor, 0.12),
                    }}
                >
                    <div
                        className="h-full rounded-full"
                        style={{
                            width: `${progressPercent}%`,
                            backgroundColor: hexToRgba(primaryColor, 0.7),
                            transition: 'width 0.4s ease-out',
                        }}
                    />
                </div>
                <span className="text-xs shrink-0" style={{ color: hexToRgba(primaryColor, 0.5) }}>
                    {Math.round(progressPercent)}%
                </span>
            </div>

            <style>{`
                @keyframes roseProfilingProgressSlideIn {
                    from { opacity: 0; transform: translateY(6px) scaleX(0.92); }
                    to { opacity: 1; transform: translateY(0) scaleX(1); }
                }
                @media (prefers-reduced-motion: reduce) {
                    .rose-profiling-progress {
                        animation: none !important;
                        opacity: 1 !important;
                        transform: none !important;
                    }
                }
            `}</style>
        </div>
    );
};
